import React, { Fragment, useContext, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Toaster, toast } from "react-hot-toast";
import { GlobalContext } from "../../../Context/GlobalContext";
import { Plus, Trash2, RefreshCcw, Tag, LayoutList, X } from "lucide-react";

import useDining from "../../Services/OrderServices/DiningServices/useDining";

/** small helper */
const cls = (...s) => s.filter(Boolean).join(" ");

const STATIC_USER_ID = "A1001";

export default function AdminSpecialOffers({ open, onClose }) {

  const {getSpecialOffers, insertSpecialOffer, deleteSpecialOffer}=useDining();

  const { api } = useContext(GlobalContext);

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [offerTitle, setOfferTitle] = useState("");
  const [offerType, setOfferType] = useState("");

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const resetForm = () => {
    setOfferTitle("");
    setOfferType("");
  };

const fetchOffers = async () => {
  setLoading(true);

  try {
    const result =
      await getSpecialOffers(STATIC_USER_ID);

    if (result.success) {
      setOffers(result.data);
    } else {
      setOffers([]);
      // optional:
      // toast.error(result.message);
    }
  } catch {
    setOffers([]);
    toast.error("Error fetching offers");
  } finally {
    setLoading(false);
  }
};


const handleCreate = async () => {
  if (!offerTitle.trim() || !offerType.trim())
    return;

  setSaving(true);

  try {
    const result = await insertSpecialOffer({
      userid: STATIC_USER_ID,
      offerTitle: offerTitle.trim(),
      offerType: offerType.trim(),
    });

    if (result.success) {
      toast.success(result.message);
      resetForm();
      fetchOffers();
    } else {
      toast.error(result.message);
    }
  } finally {
    setSaving(false);
  }
};


const handleDelete = async (id) => {
  if (!id) return;

  setDeletingId(id);

  try {
    const result = await deleteSpecialOffer(id);

    if (result.success) {
      toast.success(result.message);

      // remove locally without refetch
      setOffers((prev) =>
        prev.filter(
          (o) => String(o.id) !== String(id)
        )
      );
    } else {
      toast.error(result.message);
    }
  } finally {
    setDeletingId(null);
  }
};


  // load whenever modal opens
  useEffect(() => {
    if (open) {
      fetchOffers();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Toaster position="top-right" />
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-3xl rounded-2xl bg-white p-5 shadow-xl">
              {/* Header */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
                    <LayoutList size={18} />
                  </div>
                  <div>
                    <Dialog.Title className="text-base font-semibold">
                      Special Offers
                    </Dialog.Title>
                    <p className="text-xs text-gray-500">
                      Create, view and delete special offers (Offer / Tag master)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-xs hover:bg-gray-50 disabled:opacity-60"
                    onClick={fetchOffers}
                    disabled={loading}
                  >
                    <RefreshCcw size={14} />
                    {loading ? "Refreshing…" : "Refresh"}
                  </button>
                  <button
                    className="inline-flex h-9 items-center gap-1 rounded-xl border px-3 text-xs"
                    onClick={onClose}
                  >
                    <X size={14} />
                    Close
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.2fr)]">
                {/* Left: Create / edit form */}
                <div className="rounded-2xl border bg-white p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <div className="grid h-8 w-8 place-items-center rounded-lg bg-[#01C37D]/10 text-[#01C37D]">
                      <Plus size={16} />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Add New Offer</div>
                      <div className="text-xs text-gray-500">
                        Define offer title and tag / type.
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm">
                      <div className="mb-1 text-xs text-gray-500">
                        Offer Title
                      </div>
                      <input
                        className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                        placeholder="e.g. Flat 20% OFF on first order"
                        value={offerTitle}
                        onChange={(e) => setOfferTitle(e.target.value)}
                      />
                    </label>

                    <label className="block text-sm">
                      <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                        <span>Offer Type / Tag *</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-gray-50 text-gray-500">
                          <Tag size={16} />
                        </div>
                        <input
                          className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                          placeholder="e.g. WELCOME, FESTIVE, LOYALTY"
                          value={offerType}
                          onChange={(e) => setOfferType(e.target.value)}
                        />
                      </div>
                    </label>

                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        className="h-9 rounded-xl border px-3 text-sm"
                        onClick={resetForm}
                        type="button"
                      >
                        Clear
                      </button>
                      <button
                        className="h-9 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white disabled:opacity-60"
                        onClick={handleCreate}
                        disabled={
                          !offerTitle.trim() || !offerType.trim() || saving
                        }
                        type="button"
                      >
                        {saving ? "Saving…" : "Save Offer"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right: Offer list */}
                <div className="rounded-2xl border bg-white p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">
                        Existing Offers
                      </div>
                      <div className="text-xs text-gray-500">
                        {loading
                          ? "Loading offers…"
                          : offers.length
                          ? `Total ${offers.length} offers`
                          : "No offers yet. Add your first offer."}
                      </div>
                    </div>
                  </div>

                  <div className="max-h-[55vh] space-y-2 overflow-auto pr-1">
                    {loading && offers.length === 0 && (
                      <div className="rounded-xl border bg-gray-50 px-3 py-2 text-xs text-gray-500">
                        Fetching offers…
                      </div>
                    )}

                    {!loading && offers.length === 0 && (
                      <div className="rounded-xl border bg-gray-50 px-3 py-2 text-xs text-gray-500">
                        No special offers configured yet.
                      </div>
                    )}

                    {offers.map((offer) => (
                      <div
                        key={offer.id || offer.pk_offer_id || `${offer.offerTitle}-${offer.offerType}`}
                        className="flex items-center justify-between rounded-xl border px-3 py-2 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="truncate font-medium">
                            {offer.offerTitle || offer.title || "Untitled offer"}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5">
                              <Tag size={12} className="mr-1 opacity-70" />
                              {offer.offerType || offer.tag || "N/A"}
                            </span>
                            {offer.id && (
                              <span className="text-[10px] text-gray-400">
                                ID: {offer.id}
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          className={cls(
                            "inline-flex items-center gap-1 rounded-xl border px-2 py-1 text-xs hover:bg-red-50",
                            deletingId === offer.id
                              ? "border-red-300 text-red-600"
                              : "border-gray-200 text-gray-600"
                          )}
                          onClick={() => handleDelete(offer.id)}
                          disabled={!offer.id || deletingId === offer.id}
                          type="button"
                        >
                          <Trash2 size={13} />
                          {deletingId === offer.id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
